/*
 * @author: wayou
 * @date: 2018-09-04 15:40:16
 * @description: 全屏看图
 */

import { $window, $document, appendChild } from '@umu/umu-dom';
import React, { Component, ComponentClass } from 'react';
import ReactDOM from 'react-dom';
import ImgGallery from '../imgGallery/imgGallery';
import './fullImgGallery.scss';
import Swipe from './swipe';
import { IGalleryImg } from '@umu/umu-types';

import Portal from '../portal';

enum KEY_CODES {
    LEFT = 37,
    RIGHT = 39,
    ESC = 27,
}

interface IFullImgGalleryProps {
    /**
     * 当前播放第 ${index} 张
     */
    index: number;

    /**
     * 图片列表
     */
    imgList: IGalleryImg[];

    className?: string;
    disableDrag?: boolean;

    /**
     * 退出全屏看图
     */
    onBack?: (e?: React.MouseEvent) => void;

    /**
     * 自定义header
     */
    renderHeader?: (index: number, length: number) => JSX.Element;
}

interface IFullImgGalleryState {
    index: number;
    isHovered: boolean;
}

class FullImgGallery extends Component<IFullImgGalleryProps, IFullImgGalleryState> {
    public static defaultProps: Partial<IFullImgGalleryProps> = {
        index: 0,
        imgList: [],
    };
    private swiper?: Swipe;
    private interactiveTimer: number = 0;
    private isCanSwipe: boolean = true;

    constructor(props: IFullImgGalleryProps) {
        super(props);
        this.state = {
            index: props.index!,
            isHovered: true,
        };
    }
    public getDerivedStateFromProps(nextProps: IFullImgGalleryProps) {
        const { index } = this.state;
        const { index: newIndex } = nextProps;
        // state无更新时return null
        if (index !== newIndex) {
            return {
                index: newIndex,
            };
        } else {
            return null;
        }
    }

    public componentDidMount() {
        this.initialize();
    }

    public componentDidUpdate(prevProps: IFullImgGalleryProps) {
        if (this.props.index !== prevProps.index) {
            this.setState({
                index: this.props.index,
            });
        }
    }

    public componentWillUnmount() {
        window.removeEventListener('keydown', this.keyboardHandler);
        if (this.swiper) {
            this.swiper.destroy();
        }
        clearTimeout(this.interactiveTimer);
    }

    public onInteractive = () => {
        this.setState({
            isHovered: true,
        });
        clearTimeout(this.interactiveTimer);
        this.interactiveTimer = $window.setTimeout(() => {
            this.offInteractive();
        }, 5 * 1000);
    };

    public offInteractive = () => {
        this.setState({
            isHovered: false,
        });
        clearTimeout(this.interactiveTimer);
        this.interactiveTimer = 0;
    };

    public render() {
        const { className, imgList, disableDrag } = this.props;
        const { index } = this.state;

        return (
            <Portal preventBodyScroll={true}>
                <div className={`full-img-gallery ${className || ''}`} onClick={this.onInteractive}>
                    {this.renderHeader()}
                    {this.renderControls()}
                    <div onClick={this.back}>
                        <ImgGallery
                            index={index}
                            imgList={imgList}
                            isFullScreen={true}
                            disableDrag={disableDrag}
                            onSwipeChange={this.onSwipeChange}
                        />
                    </div>
                </div>
            </Portal>
        );
    }

    private renderHeader() {
        const { imgList, renderHeader } = this.props;
        const { index } = this.state;

        if (renderHeader) {
            return <div className="full-img-gallery-header">{renderHeader(index + 1, imgList.length)}</div>;
        }

        return (
            <div className="full-img-gallery-header">
                <span className="icon icon-back" onClick={this.back} />
                <span className="gallery-info">
                    {index + 1}/{imgList.length}
                </span>
            </div>
        );
    }

    private renderControls() {
        // if (!this.state.isHovered || this.props.imgList.length <= 1) {
        //     return null;
        // }
        if (this.props.imgList.length <= 1) {
            return null;
        }
        return (
            <div className="full-img-gallery-controls">
                <span className="icon  icon-prev" onClick={this.playPrev}>
                    <span className="icon-switch-slide"></span>
                </span>
                <span className="icon icon-next" onClick={this.playNext}>
                    <span className="icon-switch-slide"></span>
                </span>
            </div>
        );
    }

    private back = (e?: React.MouseEvent): void => {
        if (e) {
            e.stopPropagation();
            e.preventDefault();
        }
        const { onBack } = this.props;
        if (onBack) {
            onBack(e);
        }
    };

    private onSwipeChange = (isCanSwipe: boolean) => {
        this.isCanSwipe = isCanSwipe;
    };

    /**
     * 方向键处理
     */
    private initialize() {
        window.addEventListener('keydown', this.keyboardHandler);
        this.initialSwipe();
        this.onInteractive();
    }

    private initialSwipe() {
        const swiper = new Swipe('.img-slides');
        swiper.onSwipeLeft(() => {
            if (!this.isCanSwipe) {
                return;
            }
            this.playNext();
        });
        swiper.onSwipeRight(() => {
            if (!this.isCanSwipe) {
                return;
            }
            this.playPrev();
        });
    }

    private keyboardHandler = (event: KeyboardEvent): void => {
        if (event.keyCode === KEY_CODES.RIGHT) {
            this.playNext();
        }
        if (event.keyCode === KEY_CODES.LEFT) {
            this.playPrev();
        }

        if (event.keyCode === KEY_CODES.ESC) {
            this.back();
        }
    };

    /**
     * 播放下一张
     */
    private playNext = (): void => {
        const { imgList } = this.props;
        const { index } = this.state;
        let newIndex = index + 1;
        newIndex = newIndex > imgList.length - 1 ? 0 : newIndex;
        this.setState({
            index: newIndex,
        });
    };

    /**
     * 播放上一张
     */
    private playPrev = (): void => {
        const { imgList } = this.props;
        const { index } = this.state;
        let newIndex = index - 1;
        newIndex = newIndex < 0 ? imgList.length - 1 : newIndex;
        this.setState({
            index: newIndex,
        });
    };
}

export default FullImgGallery;

function wrapService<T extends IFullImgGalleryProps>(component: ComponentClass<T>) {
    const wrap = (props: T) => {
        const WrapComponent = component;
        const hostEl = $document.createElement('div');
        // 为了保持该组件和swap的组件功能一致，没有修改组件内部调用portal的逻辑，最终表现是这会是一个空标签
        hostEl.className = 'full-img-gallery-host';
        appendChild(hostEl, '');

        function destroy() {
            const unmountResult = ReactDOM.unmountComponentAtNode(hostEl);
            if (unmountResult && hostEl.parentNode) {
                hostEl.parentNode.removeChild(hostEl);
            }
        }
        function render() {
            ReactDOM.render(<WrapComponent {...props} onBack={destroy} />, hostEl);
        }

        render();
    };

    return wrap;
}

export const showFullImgGallery = wrapService(FullImgGallery);

export { Swipe };
